import { useCallback, useEffect, useRef, useState } from 'react';

type SessionTimeoutOptions = {
  timeoutMs: number;
  warningBeforeMs: number;
  onReloadNow: () => Promise<void>;
  onExpire: () => void;
  enabled?: boolean;
};

type SessionTimeoutState = {
  showWarning: boolean;
  remainingMs: number;
  reloadApplication: () => Promise<void>;
  dismissWarning: () => void;
};

export function useSessionTimeout({
  timeoutMs,
  warningBeforeMs,
  onReloadNow,
  onExpire,
  enabled = true,
}: SessionTimeoutOptions): SessionTimeoutState {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingMs, setRemainingMs] = useState(timeoutMs);

  const expiresAtRef = useRef<number>(0);
  const expiredRef = useRef(false);
  const showWarningRef = useRef(false);
  const lastMouseMoveAtRef = useRef<number>(0);

  const resetTimeoutFromActivity = useCallback(() => {
    const now = Date.now();
    expiresAtRef.current = now + timeoutMs;
    expiredRef.current = false;
    showWarningRef.current = false;
    setShowWarning(false);
    setRemainingMs(timeoutMs);
  }, [timeoutMs]);

  useEffect(() => {
    if (!enabled) {
      const now = Date.now();
      expiresAtRef.current = now + timeoutMs;
      expiredRef.current = false;
      showWarningRef.current = false;
      setShowWarning(false);
      setRemainingMs(timeoutMs);
      return;
    }

    const now = Date.now();
    expiresAtRef.current = now + timeoutMs;
    expiredRef.current = false;
    showWarningRef.current = false;

    // Only reset on explicit user interactions.
    const activityEvents: Array<keyof WindowEventMap> = [
      'click',
      'keydown',
      'pointerdown',
      'touchstart',
    ];

    const onActivity = () => {
      if (expiredRef.current) {
        return;
      }
      // Reset during the warning window too (WCAG 2.2.1 / G198: user can extend by interacting).
      resetTimeoutFromActivity();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        onActivity();
      }
    };

    const onMouseMove = () => {
      const t = Date.now();
      // Throttle mousemove activity updates to avoid resetting every pixel move.
      if (t - lastMouseMoveAtRef.current < 1000) {
        return;
      }
      lastMouseMoveAtRef.current = t;
      onActivity();
    };

    activityEvents.forEach((eventName) => window.addEventListener(eventName, onActivity, true));
    window.addEventListener('mousemove', onMouseMove, true);
    document.addEventListener('visibilitychange', onVisibilityChange);

    const interval = window.setInterval(() => {
      const remaining = Math.max(0, expiresAtRef.current - Date.now());
      const inWarningWindow = remaining <= warningBeforeMs && remaining > 0;

      if (inWarningWindow) {
        if (!showWarningRef.current) {
          showWarningRef.current = true;
          setShowWarning(true);
        }
        // Only tick React state while the warning UI is shown (countdown).
        setRemainingMs(remaining);
      }

      if (remaining === 0 && !expiredRef.current) {
        expiredRef.current = true;
        showWarningRef.current = false;
        setShowWarning(false);
        onExpire();
      }
    }, 1000);

    return () => {
      window.clearInterval(interval);
      activityEvents.forEach((eventName) =>
        window.removeEventListener(eventName, onActivity, true)
      );
      window.removeEventListener('mousemove', onMouseMove, true);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, timeoutMs, warningBeforeMs, onExpire, resetTimeoutFromActivity]);

  const reloadApplication = async () => {
    await onReloadNow();
    resetTimeoutFromActivity();
  };

  return {
    showWarning,
    remainingMs,
    reloadApplication,
    dismissWarning: resetTimeoutFromActivity,
  };
}
